import { useEffect, useState } from 'react';

import { type Campaign, type Content, fetchCampaigns, setContentArchived } from '../api.ts';
import { describeError } from '../data.tsx';
import { useToast } from '../toast.tsx';
import { Notice, Panel } from '../ui.tsx';
import * as wording from '../wording.ts';
import { CardFields } from './CardFields.tsx';
import { ChecklistEditor } from './ChecklistEditor.tsx';
import { PostsPanel } from './PostsPanel.tsx';

/** `/board?card=content:<id>`: the board with this content's drawer open (US-031). */
export function boardCardPath(contentId: string): string {
  return `/board?card=content:${encodeURIComponent(contentId)}`;
}

/**
 * Archives or unarchives a content and tells the operator how it went. The
 * content page's header and its Board panel share it, as the drawer does its
 * own button.
 */
export function useArchiveContent(onChanged: (content: Content) => void): {
  readonly busy: boolean;
  readonly toggle: (content: Content) => Promise<void>;
} {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const toggle = async (content: Content): Promise<void> => {
    const archived = content.archivedAt !== null;
    setBusy(true);
    try {
      onChanged(await setContentArchived(content.id, !archived));
      toast.ok(archived ? wording.boardUnarchived(content.name) : wording.boardArchived(content.name));
    } catch (cause: unknown) {
      // 409 `content_running` says why in its message.
      toast.error(describeError(cause));
    } finally {
      setBusy(false);
    }
  };
  return { busy, toggle };
}

/**
 * The content page's **Board** panel (US-031): the same campaign, publish-by
 * date, blocked reason and checklist as the card's drawer, through the same
 * components, so the two never disagree. An archived content says so here,
 * with **Unarchive**.
 */
export function ContentBoardPanel({
  content,
  archiving,
  onUnarchive,
  onChanged,
}: {
  readonly content: Content;
  readonly archiving: boolean;
  readonly onUnarchive: () => void;
  /** The content as the server answered it, after a save. */
  readonly onChanged: (content: Content) => void;
}) {
  const [campaigns, setCampaigns] = useState<readonly Campaign[]>([]);

  useEffect(() => {
    const controller = new AbortController();
    fetchCampaigns(controller.signal)
      .then(setCampaigns)
      .catch(() => {
        // Without the list the select offers only "No campaign"; the fields still save.
      });
    return () => {
      controller.abort();
    };
  }, []);

  return (
    <Panel title={wording.CONTENT_BOARD_PANEL} icon="project">
      <div className="stack">
        {content.archivedAt !== null && (
          <Notice kind="info">
            {wording.CONTENT_ARCHIVED_NOTICE}{' '}
            <button type="button" className="button button--small" disabled={archiving} onClick={onUnarchive}>
              {wording.BOARD_UNARCHIVE}
            </button>
          </Notice>
        )}
        <CardFields
          cardId={`content:${content.id}`}
          campaignId={content.campaignId}
          dueOn={content.dueOn}
          blockedReason={content.blockedReason}
          campaigns={campaigns}
          onSaved={(answer) => {
            if ('content' in answer) onChanged(answer.content);
          }}
        />
        <ChecklistEditor contentId={content.id} items={content.checklist} onSaved={onChanged} />
      </div>
    </Panel>
  );
}

/**
 * The content page's **Posts** panel (US-031): the drawer's posts, at every
 * status, folded unless the content is delivered — that is when posting is
 * the work. The panel polls the posts itself; a change re-reads the content,
 * since posting one is what places it.
 */
export function ContentPostsPanel({
  content,
  onChanged,
}: {
  readonly content: Content;
  readonly onChanged: () => Promise<void>;
}) {
  const delivered = content.status === 'delivered';
  return (
    <Panel
      // Delivering it opens the panel; a fold chosen before delivery is not kept.
      key={delivered ? 'delivered' : 'not-delivered'}
      title={wording.POSTS}
      icon="calendar"
      collapsible
      defaultOpen={delivered}
    >
      <PostsPanel contentId={content.id} content={content} onChanged={onChanged} titled={false} />
    </Panel>
  );
}
